'use client';

import React from 'react';
import { ImageModal } from './ImageModal';
import { getContentImageMeta } from '@/lib/image-meta';
import { useTranslations } from '@/hooks/useTranslations';
import { NativeImageWithLoader } from '@/components/ImageWithLoader';

type Screenshot = {
  id: string;
  url: string;
  alternativeText?: string;
};

type ScreenshotCarouselProps = {
  screenshots: Screenshot[];
  casinoName: string;
  pageTitle?: string;
  imageStartIndex?: number;
};

const ITEMS_PER_ROW = 4;

export function ScreenshotCarousel({
  screenshots,
  casinoName,
  pageTitle,
  imageStartIndex = 1,
}: ScreenshotCarouselProps) {
  const { t } = useTranslations();
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [failedIndices, setFailedIndices] = React.useState<Set<number>>(new Set());

  const hasScreenshots = screenshots && screenshots.length > 0;
  const filled = hasScreenshots ? screenshots.slice(0, ITEMS_PER_ROW) : [];
  const displayItems: (Screenshot | null)[] = Array(ITEMS_PER_ROW)
    .fill(null)
    .map((_, i) => filled[i] ?? null);

  const handleImageError = React.useCallback((index: number) => {
    setFailedIndices((prev) => new Set(prev).add(index));
  }, []);

  const handleImageClick = (index: number) => {
    const item = displayItems[index];
    if (item) {
      setCurrentIndex(index);
      setSelectedImage(item.url);
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const handleNext = () => {
    if (hasScreenshots && screenshots.length > 1) {
      const nextIndex = (currentIndex + 1) % screenshots.length;
      setCurrentIndex(nextIndex);
      setSelectedImage(screenshots[nextIndex].url);
    }
  };

  const handlePrev = () => {
    if (hasScreenshots && screenshots.length > 1) {
      const prevIndex = (currentIndex - 1 + screenshots.length) % screenshots.length;
      setCurrentIndex(prevIndex);
      setSelectedImage(screenshots[prevIndex].url);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {displayItems.map((screenshot, index) => {
          const imageMeta = getContentImageMeta({
            pageTitle: pageTitle || casinoName,
            imageNumber: imageStartIndex + index,
          });
          const showPlaceholder = !screenshot || failedIndices.has(index);
          const isPlaceholder = !screenshot;
          const PlaceholderContent = () => (
            <div className="flex h-full w-full items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-2 text-xs text-slate-500">{t('casinoDetail.noScreenshot')}</p>
              </div>
            </div>
          );
          return (
            <div
              key={screenshot?.id ?? `placeholder-${index}`}
              className={`group relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-[#15192b] transition-all duration-300 ${
                !showPlaceholder ? 'cursor-pointer hover:border-[#ffb74d]/50' : ''
              }`}
              onClick={() => !showPlaceholder && handleImageClick(index)}
            >
              {screenshot && !failedIndices.has(index) ? (
                <>
                  <NativeImageWithLoader
                    src={screenshot.url}
                    alt={imageMeta.alt}
                    title={imageMeta.title}
                    wrapperClassName="h-full w-full"
                    imageClassName="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loaderRoundedClassName="rounded-lg"
                    loading="lazy"
                    onError={() => handleImageError(index)}
                  />
                  <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/30" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <svg
                      className="h-10 w-10 text-white drop-shadow-lg"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                  </div>
                </>
              ) : (
                <PlaceholderContent />
              )}
            </div>
          );
        })}
      </div>

      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage}
          alt={getContentImageMeta({
            pageTitle: pageTitle || casinoName,
            imageNumber: imageStartIndex + currentIndex,
          }).alt}
          title={getContentImageMeta({
            pageTitle: pageTitle || casinoName,
            imageNumber: imageStartIndex + currentIndex,
          }).title}
          onClose={handleClose}
          onNext={screenshots.length > 1 ? handleNext : undefined}
          onPrev={screenshots.length > 1 ? handlePrev : undefined}
          currentIndex={currentIndex}
          totalImages={screenshots.length}
        />
      )}
    </>
  );
}
